import React, { Fragment, useEffect } from 'react';
import { ModalX } from '../../../components';
import {
    Button,
    Checkbox,
    Chip,
    IconButton,
    Input,
    List,
    ListItem,
    Option,
    Radio,
    Select,
    Spinner,
    Step,
    Stepper,
    Textarea
} from '@material-tailwind/react';
import {
    ArrowLeftIcon,
    ArrowRightIcon,
    BookOpenTextIcon,
    CalendarDays,
    ExternalLinkIcon,
    ListChecksIcon,
    PlusIcon,
    ScanQrCodeIcon,
    SearchIcon,
    SofaIcon,
    TrashIcon,
    UserIcon
} from 'lucide-react';
import {
    convertToPresentableText,
    formatNumberWithCommas,
    // getOrdinal,
    NotifyError,
    timeToDate
} from '../../../utils/functions';
import {
    CONNECTION_TYPES,
    CONNECTION_TYPES_ENUM,
    HOURLY_TIME_SLOTS_ENUM,
    OPERATIONAL_STATUSES,
    STATIC_USER_ROLES,
    VERIFICATION_TYPES,
    VERIFICATION_TYPES_ENUM
} from '../../../utils/enums';
import { useGetOffices } from '../../../custom-hooks/use-offices';
import { useGetSeats } from '../../../custom-hooks/use-seats';
import { useAddContract, useUpdateContract } from '../../../custom-hooks/use-contracts';
import { useGetUsers } from '../../../custom-hooks/use-users';
import { useConfirm } from '../../../custom-hooks/confirm-dialog';
import useDebounce from '../../../custom-hooks/useDebounce';
import { GetAvatarAndTextColor } from '../../../utils/ui-interpreters';
import moment from 'moment';
import { Link } from 'react-router-dom';

const isDeepEqual = (obj1, obj2) => {
    if (obj1 === obj2) return true;
    if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 == null || obj2 == null) return false;
    if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;

    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) return false;

    for (let key of keys1) {
        if (!keys2.includes(key) || !isDeepEqual(obj1[key], obj2[key])) return false;
    }
    return true;
};

const contractModalTabs = [
    {
        label: 'Customer',
        description: 'Select Customer',
        value: 'customer',
        icon: <UserIcon size={16} />,
    },
    {
        label: 'General',
        description: 'General Details',
        value: 'general',
        icon: <BookOpenTextIcon size={16} />,
    },
    {
        label: 'Offices',
        description: 'Select Offices',
        value: 'offices',
        icon: <ScanQrCodeIcon size={16} />,
    },
    {
        label: 'Seats',
        description: 'Select Seats',
        value: 'seats',
        icon: <SofaIcon size={16} />,
    },
    {
        label: 'Payments',
        description: 'Add Payments',
        value: 'payment',
        icon: <CalendarDays size={16} />,
    },
    {
        label: 'Summary',
        description: 'Review Details',
        value: 'summary',
        icon: <ListChecksIcon size={16} />,
    }
];


const ContractFormModal = ({
    isOpen,
    onClose,
    isUpdating,
    defaultData,
    updatingContractId,
    customer,
    onSuccess,
}) => {

    const ACTIVE_BOOKING_STATUSES = [
        VERIFICATION_TYPES.APPROVED,
        VERIFICATION_TYPES.PENDING,
        VERIFICATION_TYPES.NOTIFIED,
    ];

    const [formData, setFormData] = React.useState(defaultData);
    const [loadingAddContract, setLoadingAddContract] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState(''); // Search term for offices and seats
    const [searchCustomerTerm, setSearchCustomerTerm] = React.useState(''); // Search term for customers
    const [showAvailableOnly, setShowAvailableOnly] = React.useState(true);
    const [showAvailableOfficesOnly, setShowAvailableOfficesOnly] = React.useState(true);


    const debouncedSearchCustomerTerm = useDebounce(searchCustomerTerm, 500);
    const { confirm, ConfirmationDialog } = useConfirm();

    const { data: rawOffices, isLoading: isLoadingOffices } = useGetOffices();
    const { data: seats, isLoading: isLoadingSeats } = useGetSeats();

    const findAvailableOffices = (offices) => {
        if (!offices) return [];
        if (!showAvailableOfficesOnly) return offices;

        const ownOfficeIds = new Set(
            (formData?.selectedOffices || []).map(o => o?.id).filter(Boolean)
        );

        return offices.filter(office => {
            if (ownOfficeIds.has(office._id)) return true;
            const activeBookings = (office.bookings || []).filter(booking => {
                const contract = booking?.contract;
                const existingContractId = contract ? (typeof contract === 'string' ? contract : contract._id) : null;
                const isOwnContractDuringUpdate = isUpdating && existingContractId === updatingContractId;
                const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking.status);
                if (isOwnContractDuringUpdate || !isStatusAllowed) return false;

                const freezeData = booking.freeze?.isFrozen ? booking.freeze : null;
                if (freezeData) return false;

                return true;
            });
            return activeBookings.length === 0;
        });
    }

    const offices = findAvailableOffices(rawOffices);

    const addContractMutation = useAddContract();
    const updateContractMutation = useUpdateContract();

    const { data: users, isLoading: isLoadingUsers } = useGetUsers({
        role: [STATIC_USER_ROLES.USER],
        _search: debouncedSearchCustomerTerm,
        _limit: 1000
    });

    const initialStep = (isUpdating || customer) ? 1 : 0;

    const [activeStep, setActiveStep] = React.useState(initialStep);
    const [isLastStep, setIsLastStep] = React.useState(false);
    const [isFirstStep, setIsFirstStep] = React.useState(false);

    const isDirty = !isDeepEqual(formData, defaultData);

    useEffect(() => {
        if (!isOpen) return;
        setFormData(defaultData);
        setActiveStep(initialStep);
        setSearchTerm('');
        setSearchCustomerTerm('');
    }, [defaultData, isOpen, initialStep]);

    useEffect(() => {
        if (!isOpen || !formData?.isRenewal || isLoadingOffices) {
            return;
        }

        const availableOfficeIds = new Set((offices || []).map((office) => office._id));
        const nextSelectedOffices = (formData.selectedOffices || []).map((office) => ({
            ...office,
            unavailable: !availableOfficeIds.has(office.id),
        }));

        const currentKey = (formData.selectedOffices || []).map((o) => `${o.id}:${!!o.unavailable}`).join('|');
        const nextKey = nextSelectedOffices.map((o) => `${o.id}:${!!o.unavailable}`).join('|');

        if (currentKey !== nextKey) {
            setFormData((prev) => ({
                ...prev,
                selectedOffices: nextSelectedOffices,
            }));
        }
    }, [isOpen, formData?.isRenewal, formData.selectedOffices, offices, isLoadingOffices]);

    const handleNext = () => !isLastStep && setActiveStep((cur) => cur + 1);
    const handlePrev = () => !isFirstStep && setActiveStep((cur) => cur - 1);

    const availableTabs = contractModalTabs.filter(tab => {
        if (formData.contractType === CONNECTION_TYPES.DAY_PASS) {
            return !['seats', 'offices'].includes(tab.value);
        }
        else {
            return !['dayPass'].includes(tab.value);
        }
    })

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => {
            // If not nested, just update directly
            if (!name.includes('.')) {
                return {
                    ...prev,
                    [name]: value,
                };
            }
            // For deep nested keys, use a recursive helper
            const setDeepValue = (obj, keys, val) => {
                if (keys.length === 1) {
                    return { ...obj, [keys[0]]: val };
                }
                return {
                    ...obj,
                    [keys[0]]: setDeepValue(
                        obj && obj[keys[0]] ? obj[keys[0]] : {},
                        keys.slice(1),
                        val
                    ),
                };
            };
            const keys = name.split('.');
            return setDeepValue(prev, keys, value);
        });
    };

    // Generic handler for nested key value pairs (supports dot notation)
    const handleSelectChange = (name, value) => {
        setFormData((prev) => {
            // If not nested, just update directly
            if (!name.includes('.')) {
                return {
                    ...prev,
                    [name]: value,
                };
            }
            // For deep nested keys, use a recursive helper
            const setDeepValue = (obj, keys, val) => {
                if (keys.length === 1) {
                    return { ...obj, [keys[0]]: val };
                }
                return {
                    ...obj,
                    [keys[0]]: setDeepValue(
                        obj && obj[keys[0]] ? obj[keys[0]] : {},
                        keys.slice(1),
                        val
                    ),
                };
            };
            const keys = name.split('.');
            return setDeepValue(prev, keys, value);
        });
    };

    const handleOfficeSelection = (officeId) => {
        setFormData((prev) => {
            const isSelected = prev.selectedOffices.some(office => office.id === officeId);

            if (isSelected) {
                return {
                    ...prev,
                    selectedOffices: prev.selectedOffices.filter(office => office.id !== officeId)
                };
            } else {
                const selectedOfficeData = offices.find(o => o._id === officeId);
                return {
                    ...prev,
                    selectedOffices: [...prev.selectedOffices, {
                        id: officeId,
                        name: `Office # ${selectedOfficeData.number} (${selectedOfficeData.code})`, // For Read only purposes
                        isFullTime: !selectedOfficeData.isPartiallyAssigned,
                        startTime: '09:00',
                        endTime: '18:00',
                        monthlyRent: '',
                        advanceAmount: '',
                        securityAmount: '',
                        firstMonthRent: '',
                        firstMonthRentTouched: false,
                        startDate: moment(formData.startDate).isAfter(moment().startOf('day')) ? formData.startDate : moment().format('YYYY-MM-DD'),
                        endDate: formData.endDate,
                        // rentFrequency: { type: String, enum: ['once', 'daily', 'weekly', 'monthly', 'yearly'], default: 'monthly' },
                        rate: selectedOfficeData?.monthlyRate || 0,
                        comment: ''
                    }]
                };
            }
        });
    };

    const handleSeatSelection = (seatId) => {
        setFormData((prev) => {
            const isSelected = prev.selectedSeats.some(seat => seat.id === seatId);

            if (isSelected) {
                return {
                    ...prev,
                    selectedSeats: prev.selectedSeats.filter(seat => seat.id !== seatId)
                };
            } else {
                const selectedSeatData = seats.find(s => s._id === seatId);
                return {
                    ...prev,
                    selectedSeats: [...prev.selectedSeats, {
                        id: seatId,
                        name: `Seat # ${selectedSeatData.seatCode} - Space ${selectedSeatData.coworking.number} (${selectedSeatData.coworking.code})`, // For Read only purposes
                        isFullTime: !selectedSeatData.isPartiallyAssigned,
                        startTime: '09:00',
                        endTime: '18:00',
                        monthlyRent: '',
                        advanceAmount: '',
                        securityAmount: '',
                        firstMonthRent: '',
                        firstMonthRentTouched: false,
                        startDate: moment(formData.startDate).isAfter(moment().startOf('day')) ? formData.startDate : moment().format('YYYY-MM-DD'),
                        endDate: formData.endDate,
                        // rentFrequency: { type: String, enum: ['once', 'daily', 'weekly', 'monthly', 'yearly'], default: 'monthly' },
                        rate: selectedSeatData?.monthlyRate || 0,
                        comment: ''
                    }]
                };
            }
        });
    };

    // Suggest a prorated first-month rent when an item's booking starts mid-month, live as
    // Monthly Rent or the start date changes — but only while the admin hasn't taken manual
    // control of that item's First Month Rent field (typed into it directly, or moved the
    // start date back to the 1st, which clears and un-touches it since the field becomes
    // irrelevant and hidden). Mirrors the same logic in the standalone booking form modal.
    // Fixed 30-day-month baseline on purpose, regardless of the booking's actual calendar
    // month (28/29/30/31 days) — a simple, predictable "days remaining out of 30" estimate
    // rather than a calendar-accurate proration.
    const computeSuggestedFirstMonthRent = (startDate, monthlyRent) => {
        if (!startDate) return '';
        const start = moment(startDate);
        if (start.date() === 1) return '';
        const rent = Number(monthlyRent) || 0;
        if (rent <= 0) return '';
        const daysRemaining = Math.max(0, 30 - start.date() + 1);
        return Math.round((rent * daysRemaining) / 30);
    };

    const applyItemDetailChange = (item, field, value) => {
        const updated = { ...item, [field]: value };

        if (field === 'firstMonthRent') {
            updated.firstMonthRentTouched = true;
            return updated;
        }

        // Monthly rent / start date changes no longer auto-fill First Month Rent — it stays
        // empty (meaning "use the standard monthly rent") until the admin deliberately
        // focuses the field, at which point applyFirstMonthRentFocus below fills in a
        // suggestion. This avoids the common case — first month IS the full rent, not a
        // prorated one — silently pre-filling a value the admin then has to notice and clear.
        if (field === 'startDate') {
            const isFirstOfMonth = updated.startDate && moment(updated.startDate).date() === 1;
            if (isFirstOfMonth) {
                updated.firstMonthRent = '';
                updated.firstMonthRentTouched = false;
            }
        }

        return updated;
    };

    // Fills First Month Rent with a suggested value the first time the admin focuses the
    // (still-empty, untouched) field — not before. Once filled this way it's marked touched,
    // same as if the admin had typed into it, so it won't get silently overwritten later.
    const applyFirstMonthRentFocus = (item) => {
        if (item.firstMonthRentTouched) return item;
        if (item.firstMonthRent !== '' && item.firstMonthRent != null) return item;
        const suggested = computeSuggestedFirstMonthRent(item.startDate, item.monthlyRent);
        if (suggested === '') return item;
        return { ...item, firstMonthRent: suggested, firstMonthRentTouched: true };
    };

    const handleOfficeDetailChange = (officeId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            selectedOffices: prev.selectedOffices.map(office =>
                office.id === officeId ? applyItemDetailChange(office, field, value) : office
            )
        }));
    };

    const handleSeatDetailChange = (seatId, field, value) => {
        setFormData((prev) => ({
            ...prev,
            selectedSeats: prev.selectedSeats.map(seat =>
                seat.id === seatId ? applyItemDetailChange(seat, field, value) : seat
            )
        }));
    };

    const handleOfficeFirstMonthRentFocus = (officeId) => {
        setFormData((prev) => ({
            ...prev,
            selectedOffices: prev.selectedOffices.map(office =>
                office.id === officeId ? applyFirstMonthRentFocus(office) : office
            )
        }));
    };

    const handleSeatFirstMonthRentFocus = (seatId) => {
        setFormData((prev) => ({
            ...prev,
            selectedSeats: prev.selectedSeats.map(seat =>
                seat.id === seatId ? applyFirstMonthRentFocus(seat) : seat
            )
        }));
    };

    const buildContractPayload = () => {
        const bookings = [
            ...formData.selectedOffices.filter((o) => !o.unavailable).map((office) => ({
                office: office.id,
                dailyStartTime: !office.isFullTime ? timeToDate(office.startTime) : null,
                dailyEndTime: !office.isFullTime ? timeToDate(office.endTime) : null,
                rentRate: Number(office.rate || 0),
                rentTotal: Number(office.monthlyRent || 0),
                firstMonthRent: office.firstMonthRent === '' || office.firstMonthRent == null ? null : Number(office.firstMonthRent),
                advanceAmount: Number(office.advanceAmount || 0),
                securityAmount: Number(office.securityAmount || 0),
                startDate: office.startDate || formData.startDate,
                endDate: office.endDate || null,
                comment: office.comment
            })),
            ...formData.selectedSeats.map((seat) => ({
                seat: seat.id,
                dailyStartTime: !seat.isFullTime ? timeToDate(seat.startTime) : null,
                dailyEndTime: !seat.isFullTime ? timeToDate(seat.endTime) : null,
                rentRate: Number(seat.rate || 0),
                rentTotal: Number(seat.monthlyRent || 0),
                firstMonthRent: seat.firstMonthRent === '' || seat.firstMonthRent == null ? null : Number(seat.firstMonthRent),
                advanceAmount: Number(seat.advanceAmount || 0),
                securityAmount: Number(seat.securityAmount || 0),
                startDate: seat.startDate || formData.startDate,
                endDate: seat.endDate || null,
                comment: seat.comment
            }))
        ];

        return {
            user: customer ? customer._id : formData.user?._id,
            contractType: formData.contractType,
            startDate: formData.startDate,
            endDate: formData.endDate || null,
            status: formData.status,
            comment: formData.comment,
            reason: formData.reason,
            duration: Number(formData.duration || 0),
            noticePeriod: Number(formData.noticePeriod || 0),
            oneTimeServiceCharges: Number(formData.oneTimeServiceCharges || 0),
            oneTimeOtherCharges: formData.oneTimeOtherCharges || [],
            bookings,
        };
    };

    const isDateRangeOverlapping = (existingStartDate, existingEndDate, newStartDate, newEndDate) => {
        if (!existingStartDate || !newStartDate) {
            return false;
        }

        const existingStart = moment(existingStartDate).startOf('day');
        const existingEnd = existingEndDate ? moment(existingEndDate).endOf('day') : null;
        const nextStart = moment(newStartDate).startOf('day');
        const nextEnd = newEndDate ? moment(newEndDate).endOf('day') : null;

        const existingStartsBeforeNewEnds = !nextEnd || existingStart.isSameOrBefore(nextEnd);
        const newStartsBeforeExistingEnds = !existingEnd || nextStart.isSameOrBefore(existingEnd);

        return existingStartsBeforeNewEnds && newStartsBeforeExistingEnds;
    };

    const isBookingFullTime = (booking) => !(booking?.dailyStartTime && booking?.dailyEndTime);

    const getBookingContractId = (booking) => {
        if (!booking?.contract) {
            return null;
        }
        if (typeof booking.contract === 'string') {
            return booking.contract;
        }
        return booking.contract?._id || null;
    };

    const getBookingUserName = (booking) => {
        if (typeof booking?.user === 'object' && booking?.user?.name) {
            return booking.user.name;
        }
        if (booking?.contract?.user?.name) {
            return booking.contract.user.name;
        }
        return 'Deleted User';
    };

    const getUsersListText = (bookings = []) => {
        const users = [...new Set(bookings.map((booking) => getBookingUserName(booking)).filter(Boolean))];
        return users.length > 0 ? users.join(', ') : 'Deleted User';
    };

    const getSeatDisplayName = (seat) => `Seat # ${seat?.seatCode || 'N/A'}`;

    const validateSeatBookingConflicts = (bookings = []) => {
        const seatBookings = bookings.filter((booking) => booking?.seat);

        for (const seatBooking of seatBookings) {
            const selectedSeat = seats?.find((seat) => seat?._id === seatBooking.seat);

            if (!selectedSeat) {
                continue;
            }

            const overlappingBookings = (selectedSeat.bookings || []).filter((existingBooking) => {
                const existingContractId = getBookingContractId(existingBooking);
                if (!existingContractId) return false;
                const isOwnContractDuringUpdate = isUpdating && existingContractId === updatingContractId;
                const isStatusAllowed = !existingBooking?.status || ACTIVE_BOOKING_STATUSES.includes(existingBooking.status);

                if (isOwnContractDuringUpdate || !isStatusAllowed) {
                    return false;
                }

                const isOverlapping = isDateRangeOverlapping(
                    existingBooking.startDate,
                    existingBooking.endDate,
                    seatBooking.startDate,
                    seatBooking.endDate
                );

                if (!isOverlapping) return false;

                const freezeData = existingBooking.freeze?.isFrozen ? existingBooking.freeze : (existingBooking.contract?.freeze?.isFrozen ? existingBooking.contract.freeze : null);

                if (freezeData && freezeData.freezeStartDate && freezeData.freezeEndDate) {
                    const freezeStart = moment(freezeData.freezeStartDate).startOf('day');
                    const freezeEnd = moment(freezeData.freezeEndDate).endOf('day');

                    const overlapsBeforeFreeze = isDateRangeOverlapping(
                        existingBooking.startDate,
                        freezeStart.clone().subtract(1, 'days'),
                        seatBooking.startDate,
                        seatBooking.endDate
                    );

                    const overlapsAfterFreeze = isDateRangeOverlapping(
                        freezeEnd.clone().add(1, 'days'),
                        existingBooking.endDate,
                        seatBooking.startDate,
                        seatBooking.endDate
                    );

                    return overlapsBeforeFreeze || overlapsAfterFreeze;
                }

                return true;
            });

            if (overlappingBookings.length === 0) {
                continue;
            }

            const fullTimeOverlappingBookings = overlappingBookings.filter((booking) => isBookingFullTime(booking));
            const partTimeOverlappingBookings = overlappingBookings.filter((booking) => !isBookingFullTime(booking));
            const isNewBookingFullTime = isBookingFullTime(seatBooking);

            if (fullTimeOverlappingBookings.length > 0) {
                throw new Error(
                    `${getSeatDisplayName(selectedSeat)} is already booked full time for the following user(s): ${getUsersListText(fullTimeOverlappingBookings)}.`
                );
            }

            if (isNewBookingFullTime && partTimeOverlappingBookings.length > 0) {
                throw new Error(
                    `${getSeatDisplayName(selectedSeat)} is booked for part time for the following user(s): ${getUsersListText(partTimeOverlappingBookings)} and can be booked part time only.`
                );
            }

            // Part-time vs Part-time: check for time overlap
            const selectedSeatFormEntry = formData.selectedSeats.find(s => s.id === seatBooking.seat);
            if (selectedSeatFormEntry && !selectedSeatFormEntry.isFullTime && partTimeOverlappingBookings.length > 0) {
                const newStartMin = toMinutes(selectedSeatFormEntry.startTime);
                const newEndMin = toMinutes(selectedSeatFormEntry.endTime);

                const timeConflicts = partTimeOverlappingBookings.filter((existing) => {
                    const existStartMin = toMinutes(moment(existing.dailyStartTime).format('HH:mm'));
                    const existEndMin = toMinutes(moment(existing.dailyEndTime).format('HH:mm'));
                    return timesOverlap(newStartMin, newEndMin, existStartMin, existEndMin);
                });

                if (timeConflicts.length > 0) {
                    throw new Error(
                        `${getSeatDisplayName(selectedSeat)} has overlapping part-time bookings for the selected time slot. Booked by: ${getUsersListText(timeConflicts)}.`
                    );
                }
            }
        }
    };

    const isBookingActiveForStatusChange = (booking, at = moment()) => {
        if (!booking.startDate) {
            return false;
        }

        const bookingStartDate = moment(booking.startDate);
        const bookingEndDate = booking.endDate ? moment(booking.endDate) : null;

        if (at.isBefore(bookingStartDate)) {
            return true;
        }

        if (!bookingEndDate) {
            return at.isSameOrAfter(bookingStartDate);
        }

        return at.isSameOrAfter(bookingStartDate) && at.isBefore(bookingEndDate);
    };

    const getActiveBookingsForStatusChange = (bookings = []) => {
        const now = moment();
        return bookings.filter((booking) => isBookingActiveForStatusChange(booking, now));
    };

    const statusesBlockedWithActiveBookings = [
        VERIFICATION_TYPES.PENDING,
        VERIFICATION_TYPES.COMPLETED,
        VERIFICATION_TYPES.CANCELLED,
        VERIFICATION_TYPES.REJECTED,
        VERIFICATION_TYPES.NOTIFIED,
    ];

    const statusChangeBlockedByActiveBookings =
        statusesBlockedWithActiveBookings.includes(formData.status) &&
        getActiveBookingsForStatusChange([
            ...formData.selectedOffices,
            ...formData.selectedSeats,
        ]).length > 0;

    const handleFormSubmit = async (e) => {
        e.preventDefault();

        try {
            const payload = buildContractPayload();

            if (!payload.user) {
                throw new Error('Customer is required');
            }

            if (!payload.startDate) {
                throw new Error('Contract start date is required');
            }

            if (payload.endDate && moment(payload.startDate).isAfter(moment(payload.endDate))) {
                throw new Error('Contract end date cannot be before contract start date');
            }

            if (!isUpdating && payload.contractType !== CONNECTION_TYPES.DAY_PASS && payload.bookings.length === 0) {
                throw new Error('Please add at least one office or seat booking');
            }

            for (const booking of payload.bookings) {
                if (!booking.startDate) {
                    throw new Error('Each booking must have a start date');
                }
                if (booking.endDate && moment(booking.startDate).isAfter(moment(booking.endDate))) {
                    throw new Error('Booking end date cannot be before booking start date');
                }
                if (payload.endDate && booking.endDate && moment(booking.endDate).isAfter(moment(payload.endDate), 'day')) {
                    throw new Error('Booking end date cannot be greater than the contract end date');
                }
            }

            const initialSeatIds = new Set((defaultData?.selectedSeats || []).map((seat) => seat?.id).filter(Boolean));
            const seatConflictValidationBookings = isUpdating
                ? payload.bookings.filter((booking) => !booking?.seat || !initialSeatIds.has(booking.seat))
                : payload.bookings;

            validateSeatBookingConflicts(seatConflictValidationBookings);

            const activeBookings = getActiveBookingsForStatusChange(payload.bookings);

            if (statusesBlockedWithActiveBookings.includes(payload.status) && activeBookings.length > 0) {
                if (payload.status !== VERIFICATION_TYPES.COMPLETED) {
                    throw new Error(`Contracts cannot be marked as ${payload.status} while there is a booking active.`);
                }

                const today = moment().format('YYYY-MM-DD');
                const activeBookingList = [
                    ...formData.selectedOffices,
                    ...formData.selectedSeats,
                ].filter((booking) => isBookingActiveForStatusChange(booking));

                const confirmation = await confirm({
                    title: 'Complete Contract and Active Bookings',
                    message: (
                        <div className="space-y-2">
                            <p>
                                The following bookings are active. Completing this contract will also complete these bookings on {moment(today).format('MMM DD, YYYY')}.
                            </p>
                            <ul className="list-disc pl-5">
                                {activeBookingList.map((booking) => (
                                    <li key={booking.id}>{booking.name || 'Booking'}</li>
                                ))}
                            </ul>
                        </div>
                    ),
                    confirmText: 'Yes, Complete All',
                    cancelText: 'Cancel',
                    confirmBtnColor: 'green',
                });

                if (!confirmation) {
                    return;
                }

                payload.endDate = today;
                payload.bookings = payload.bookings.map((booking) => {
                    if (!isBookingActiveForStatusChange(booking)) {
                        return booking;
                    }
                    return {
                        ...booking,
                        endDate: today,
                    };
                });
            }

            if (isUpdating) {
                const today = moment().startOf('day');
                const endDateMoment = payload.endDate ? moment(payload.endDate).startOf('day') : null;
                const startDateMoment = payload.startDate ? moment(payload.startDate).startOf('day') : null;

                if (endDateMoment && endDateMoment.isSameOrBefore(today) && payload.status !== VERIFICATION_TYPES.COMPLETED) {
                    const confirmation = await confirm({
                        title: 'Complete Contract',
                        message: 'The end date is in the past or today. This contract will be marked as completed immediately. Do you want to proceed?',
                        confirmText: 'Yes, Complete',
                        cancelText: 'Cancel',
                        confirmBtnColor: 'green'
                    });
                    if (!confirmation) {
                        return;
                    }
                } else if (startDateMoment && startDateMoment.isAfter(today) && payload.status !== VERIFICATION_TYPES.PENDING) {
                    const confirmation = await confirm({
                        title: 'Mark Contract as Pending',
                        message: 'The start date is in the future. This contract will be marked as pending until the start date. Do you want to proceed?',
                        confirmText: 'Yes, Proceed',
                        cancelText: 'Cancel',
                        confirmBtnColor: 'blue'
                    });
                    if (!confirmation) {
                        return;
                    }
                }
            }

            setLoadingAddContract(true);

            if (isUpdating) {
                await updateContractMutation.mutateAsync({
                    contractId: updatingContractId,
                    data: payload,
                });
            } else {
                await addContractMutation.mutateAsync(payload);
            }

            onClose();
            setFormData(defaultData);
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            NotifyError(error?.response?.data?.message || error?.message || 'Failed to save contract', error);
        } finally {
            setLoadingAddContract(false);
        }
    };

    const filteredOffices = offices?.filter(office =>
        office.code.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    const filteredSeats = seats?.filter(seat =>
        seat.seatCode.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

    // Filter out seats that have ANY active full-time bookings if toggle is on
    const findAvailableSeats = (allSeats) => {
        if (!showAvailableOnly) return allSeats;

        // Collect seat IDs that are selected in the form
        const ownSeatIds = new Set(
            (formData?.selectedSeats || []).map(s => s?.id).filter(Boolean)
        );

        return allSeats.filter(seat => {
            // Always show seats that are selected
            if (ownSeatIds.has(seat._id)) return true;

            const activeBookings = (seat.bookings || []).filter(booking => {
                const existingContractId = getBookingContractId(booking);
                const isOwnContractDuringUpdate = isUpdating && existingContractId === updatingContractId;
                const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking.status);
                if (isOwnContractDuringUpdate || !isStatusAllowed) return false;

                // Exclude frozen bookings
                const freezeData = booking.freeze?.isFrozen ? booking.freeze : null;
                if (freezeData) return false;

                return true; // No date filtering
            });

            // If ANY active booking is full-time → seat is hidden
            const hasFullTimeBooking = activeBookings.some(b => isBookingFullTime(b));
            if (hasFullTimeBooking) return false;

            // Seat is available (vacant or only has part-time bookings)
            return true;
        });
    };

    const availableFilteredSeats = findAvailableSeats(
        filteredSeats.filter(seat => seat.status === OPERATIONAL_STATUSES.OPERATIONAL)
    );

    // Compute time conflict for a specific seat in the contract form.
    // No date range filter here — warns as soon as time is entered, regardless of whether
    // contract/seat start-end dates have been set yet.
    const toMinutes = (hhmm) => {
        if (!hhmm) return null;
        const [h, m] = String(hhmm).split(':').map(Number);
        return h * 60 + m;
    };

    const timesOverlap = (aStart, aEnd, bStart, bEnd) => {
        if (aStart === null || aEnd === null || bStart === null || bEnd === null) return false;
        return aStart < bEnd && bStart < aEnd;
    };

    const computeSeatTimeConflict = (seatId) => {
        const selectedSeatEntry = formData.selectedSeats.find(s => s.id === seatId);
        if (!selectedSeatEntry || selectedSeatEntry.isFullTime) return null;

        const newStartMin = toMinutes(selectedSeatEntry.startTime);
        const newEndMin = toMinutes(selectedSeatEntry.endTime);
        if (newStartMin === null || newEndMin === null || newStartMin >= newEndMin) return null;

        const seatData = seats?.find(s => s._id === seatId);
        if (!seatData) return null;

        // Check all active non-frozen part-time bookings — no date range filter so warning
        // fires immediately without requiring contract dates to be set.
        const partTimeBookings = (seatData.bookings || []).filter(booking => {
            const existingContractId = getBookingContractId(booking);
            const isOwnContractDuringUpdate = isUpdating && existingContractId === updatingContractId;
            const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking.status);
            if (isOwnContractDuringUpdate || !isStatusAllowed) return false;
            const freezeData = booking.freeze?.isFrozen ? booking.freeze : null;
            if (freezeData) return false;
            return !isBookingFullTime(booking);
        });

        if (partTimeBookings.length === 0) return null;

        const timeConflicts = partTimeBookings.filter((existing) => {
            const existStartMin = toMinutes(moment(existing.dailyStartTime).format('HH:mm'));
            const existEndMin = toMinutes(moment(existing.dailyEndTime).format('HH:mm'));
            return timesOverlap(newStartMin, newEndMin, existStartMin, existEndMin);
        });

        if (timeConflicts.length === 0) return null;

        return `Time slot (${selectedSeatEntry.startTime} – ${selectedSeatEntry.endTime}) overlaps with existing booking(s) by: ${getUsersListText(timeConflicts)}.`;
    };

    const calculateSummary = (offices, seats) => {
        if (formData.contractType === CONNECTION_TYPES.DAY_PASS) {
            const totalRent = Number(formData.dayPass.totalPrice || 0)
            return {
                totalOffices: 0,
                totalSeats: 0,
                totalBookings: 0,
                recommendedSecurity: 0,
                totalSecurity: 0,
                totalRent,
                totalPayable: totalRent,
                rentType: formData.dayPass.priceType,
            }
        }
        else {
            const availableOffices = offices.filter((o) => !o.unavailable);
            const availableSeats = seats.filter((s) => !s.unavailable);
            const totalRent =
                availableOffices.reduce((acc, office) => acc + Number(office.monthlyRent || 0), 0) +
                availableSeats.reduce((acc, seat) => acc + Number(seat.monthlyRent || 0), 0);
            const recommendedSecurity = totalRent * 2;
            const totalAdvance =
                availableOffices.reduce((acc, office) => acc + Number(office.advanceAmount || 0), 0) +
                availableSeats.reduce((acc, seat) => acc + Number(seat.advanceAmount || 0), 0);
            const totalSecurity =
                availableOffices.reduce((acc, office) => acc + Number(office.securityAmount || 0), 0) +
                availableSeats.reduce((acc, seat) => acc + Number(seat.securityAmount || 0), 0);

            return {
                totalOffices: availableOffices.length,
                totalSeats: availableSeats.length,
                totalBookings: availableOffices.length + availableSeats.length,
                recommendedSecurity,
                totalAdvance,
                totalSecurity,
                totalRent,
                totalPayable: totalRent + totalSecurity + totalAdvance,
                rentType: 'monthly'
            }
        }
    }

    const summary = calculateSummary(formData.selectedOffices, formData.selectedSeats);

    const handleSelectCustomer = (user) => {
        setFormData((prev) => ({
            ...prev,
            user: user
        }));
    }

    const renderCustomerSelection = () => customer ? (
        <div className="h-[390px] flex justify-center items-center">
            <div className="px-1 md:px-4">
                <div className="flex flex-col gap-2 items-center">
                    <div className={`w-24 h-24 rounded-full bg-gray-200 border flex items-center justify-center`}>
                        {
                            customer?.name?.split(' ').map((name, index) => (
                                <span key={index} className={`text-2xl font-bold capitalize`}>
                                    {name.charAt(0)}
                                </span>
                            )) || '?'
                        }
                    </div>
                    <span className="text-sm font-medium">{convertToPresentableText(customer?.name) || 'Deleted Customer'}</span>
                    <span className="text-xs text-gray-600">{customer?.email || 'N/A'}</span>
                    <span className="text-xs text-gray-600">{customer.phone}</span>
                </div>
                <hr className='my-4' />
                <div className="flex justify-end gap-2">
                    {isUpdating && isDirty && (
                        <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                            {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                        </Button>
                    )}
                    <Button onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                        Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                    </Button>
                </div>
            </div>

        </div>
    ) : (
        <div className="space-y-4">
            <div className="px-1 md:px-4">
                <Input
                    label="Search Customer"
                    placeholder="Search by name, email or phone"
                    value={searchCustomerTerm}
                    onChange={(e) => setSearchCustomerTerm(e.target.value)}
                    icon={<SearchIcon className="h-4 w-4" />}
                />
                <span className="text-xs text-gray-600">
                    Type the name, email or phone number to search
                </span>
            </div>
            <div className="h-[390px] overflow-y-auto">
                {
                    isLoadingUsers ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        users?.users.length > 0 ? (
                            <List>
                                {
                                    users?.users.map(user => {
                                        const { bg, text } = GetAvatarAndTextColor(user.name);
                                        return (
                                            <ListItem key={user._id} selected={formData.user?._id === user._id} onClick={() => handleSelectCustomer(user._id === formData.user?._id ? null : user)}>
                                                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 w-full ">
                                                    <div className="flex items-center gap-2">
                                                        <div className="hidden md:block">
                                                            <Checkbox
                                                                checked={formData.user?._id === user._id}
                                                                className="h-4 w-4"
                                                                ripple={false}
                                                                readOnly
                                                            />
                                                        </div>
                                                        <div className={`w-10 h-10 rounded-full ${bg} border flex items-center justify-center`}>
                                                            {
                                                                user?.name?.split(' ').map((name, index) => (
                                                                    <span key={index} className={`text-xs font-bold ${text} capitalize`}>
                                                                        {name.charAt(0)}
                                                                    </span>
                                                                )) || '?'
                                                            }
                                                        </div>
                                                        <div className="flex flex-col gap-1">
                                                            <span className="text-sm font-medium">{convertToPresentableText(user?.name) || 'Deleted User'}</span>
                                                            <span className="text-xs text-gray-600">{user?.email || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col gap-1 items-start md:items-end">
                                                        <span className="text-xs text-gray-600 hidden md:block">{user?.phone || 'N/A'}</span>
                                                        <span className="text-xs text-gray-600">Member since {moment(user.createdDate).fromNow()}</span>
                                                    </div>
                                                </div>
                                            </ListItem>
                                        )
                                    })
                                }
                            </List>
                        ) : (
                            <div className="flex justify-center items-center h-full">
                                <span className="text-sm text-gray-600">No customers found</span>
                            </div>
                        )
                    )
                }

            </div>
            <hr />
            <div className="flex justify-end gap-2">
                {isUpdating && isDirty && (
                    <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                        {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                    </Button>
                )}
                {
                    <Button disabled={!formData.user} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                        Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                    </Button>
                }
            </div>
        </div>
    )

    const renderGeneralDetails = () => (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                    label="Contract Type"
                    value={formData.contractType}

                    onChange={(value) => handleSelectChange("contractType", value)}
                >
                    {
                        CONNECTION_TYPES_ENUM.map((value) => (
                            <Option key={value} value={value}>
                                {convertToPresentableText(value)}
                            </Option>
                        ))
                    }
                </Select>

                <Select
                    label="Status"
                    value={formData.status}
                    onChange={(value) => handleSelectChange("status", value)}
                >
                    {
                        VERIFICATION_TYPES_ENUM.map((value) => (
                            <Option key={value} value={value}>
                                {convertToPresentableText(value)}
                            </Option>
                        ))
                    }
                </Select>
            </div>
            {
                statusChangeBlockedByActiveBookings && (
                    formData.status === VERIFICATION_TYPES.COMPLETED ? (
                        <p className="text-sm text-amber-600">
                            Active bookings will be listed for confirmation and completed on today&apos;s date.
                        </p>
                    ) : (
                        <p className="text-sm text-red-600">
                            Contracts cannot be marked as {formData.status} while there is a booking active.
                        </p>
                    )
                )
            }


            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Input
                    type="date"
                    label="Start Date"
                    name="startDate"
                    required
                    value={formData.startDate}
                    onChange={handleInputChange}
                />
                <Input
                    type="date"
                    label="End Date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                />
                <Input
                    type="number"
                    label="Notice Period (weeks)"
                    name="noticePeriod"
                    value={formData.noticePeriod}
                    onChange={handleInputChange}
                />
            </div>
            <div className="">
                <Input
                    label="Purpose / Reason"
                    name="reason"
                    required
                    value={formData.reason}
                    onChange={handleInputChange}
                />
                <div className="flex items-center flex-wrap gap-1 mt-1">
                    <span className="text-xs text-gray-600">Examples:</span>
                    {
                        [
                            'Freelancer',
                            'Startup',
                            'Business',
                            'Corporate',
                            'Government',
                            'Education',
                            'Other',
                        ].map((value) => (
                            <Chip
                                key={value}
                                value={`#${value}`}
                                color="blue"
                                variant="outlined"
                                className='cursor-pointer hover:bg-gray-100 hover:text-gray-800 duration-300 rounded-full'
                                size='sm'
                                onClick={() => setFormData({ ...formData, reason: value })}
                            />
                        ))
                    }
                </div>
            </div>

            <Textarea
                label="Additional Comments"
                name="comment"
                value={formData.comment}
                onChange={handleInputChange}
            />
            <hr />
            <div className="flex justify-end gap-2">
                {isUpdating && isDirty && (
                    <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                        {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                    </Button>
                )}
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={!formData.startDate || !formData.reason} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const checkSelectedOfficesSpecifiedRent = formData.selectedOffices.filter(office => parseInt(office.monthlyRent || 0) < 1000);
    const checkSelectedSeatsSpecifiedRent = formData.selectedSeats.filter(seat => parseInt(seat.monthlyRent || 0) < 1000);

    const renderOfficeSelection = () => (
        <div className="mt-4 min-h-[480px]">
            <Input
                label="Search Offices"
                placeholder="Search by office code"
                icon={<SearchIcon className="h-4 w-4" />}
                value={searchTerm}
                disabled={isLoadingOffices}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="mt-2 flex items-center justify-end">
                <Checkbox
                    checked={showAvailableOfficesOnly}
                    onChange={(e) => setShowAvailableOfficesOnly(e.target.checked)}
                    label={<span className="text-sm text-gray-700">Show available offices only</span>}
                    size="sm"
                />
            </div>
            <div className="h-[390px] overflow-y-auto mt-4">
                {
                    isLoadingOffices ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        <List>
                            {/* Show unavailable renewal offices at the top */}
                            {formData?.isRenewal && formData.selectedOffices
                                .filter((o) => o.unavailable)
                                .map((office) => (
                                    <ListItem key={office.id} className="py-1 opacity-60 cursor-not-allowed bg-red-50" disabled>
                                        <div className="flex w-full items-center">
                                            <div className="hidden md:block">
                                                <Checkbox checked={false} className="h-4 w-4" ripple={false} readOnly />
                                            </div>
                                            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
                                                <div className="font-thin">
                                                    {office.name}
                                                    <div className="text-xs text-red-500 font-semibold">
                                                        Not Available — this office is already booked for the selected dates
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </ListItem>
                                ))
                            }
                            {filteredOffices
                                .filter(office => office.status === OPERATIONAL_STATUSES.OPERATIONAL)
                                .slice() // Create a copy to avoid mutating the original array
                                .map((office) => {
                                    const activeBookings = (office.bookings || []).filter(booking => {
                                        const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking.status);
                                        return isStatusAllowed && !booking.freeze?.isFrozen;
                                    });
                                    const isBooked = activeBookings.length > 0;
                                    return (
                                        <Fragment key={office._id}>
                                            <ListItem
                                                className="py-1"
                                                // disabled={availability === 0}
                                                selected={formData.selectedOffices.some(o => o.id === office._id)}
                                                onClick={() => handleOfficeSelection(office._id)}>
                                                <div className="flex w-full items-center">
                                                    <div className="hidden md:block">
                                                        <Checkbox
                                                            checked={formData.selectedOffices.some(o => o.id === office._id)}
                                                            className="h-4 w-4"
                                                            ripple={false}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
                                                        <div className="font-thin">
                                                            Office # {office.number} ({office.code})
                                                            {isBooked && (
                                                                <span className="ml-2 font-medium text-red-500 text-xs">
                                                                    · Booked
                                                                </span>
                                                            )}
                                                            <br />
                                                            <span className="text-xs">{office.capacity} Seater - {office.monthlyRate} Per Month</span>
                                                        </div>
                                                        {/* <div className="font-thin text-xs text-gray-600">
                                                            {availability === 0 ? "Not Available" : `Available ${availability}h`} <br />
                                                            On {getOrdinal(office.floor.number)} Floor, In {office.floor.building.name}, Branch {office.floor.building.branch.name}
                                                        </div> */}
                                                    </div>
                                                </div>

                                            </ListItem>
                                            {formData.selectedOffices.some(o => o.id === office._id) && (
                                                <div className="border-l-2 border-gray-400 ml-2 pl-2">
                                                    <div className={`grid grid-cols-1 md:grid-cols-5 gap-2 mb-2`}>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Monthly Rent"
                                                                placeholder={office.monthlyRate}
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.monthlyRent || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'monthlyRent', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Advance"
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.advanceAmount || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'advanceAmount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Security"
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.securityAmount || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'securityAmount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full col-span-2">
                                                            <Input
                                                                type="text"
                                                                label="Additional Comments"
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.comment || ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'comment', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>
                                                    {formData.selectedOffices.find(o => o.id === office._id)?.startDate &&
                                                        moment(formData.selectedOffices.find(o => o.id === office._id)?.startDate).date() !== 1 && (
                                                        <div className="mb-2">
                                                            <Input
                                                                type="number"
                                                                label="First Month Rent (PKR)"
                                                                placeholder="Leave blank to use standard rent"
                                                                value={formData.selectedOffices.find(o => o.id === office._id)?.firstMonthRent ?? ''}
                                                                onChange={(e) => handleOfficeDetailChange(office._id, 'firstMonthRent', e.target.value)}
                                                                onFocus={() => handleOfficeFirstMonthRentFocus(office._id)}
                                                            />
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Enter a prorated amount if this booking starts mid-month.
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-2 mb-2`}>
                                                        <div className={`flex flex-col md:flex-row gap-2 w-full`}>
                                                            <div className={`p-2 border rounded-lg w-full`}>
                                                                <span className="text-gray-600 text-sm font-bold">Booking Period</span>
                                                                <hr className="my-1" />
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking From"
                                                                        value={formData.selectedOffices.find(o => o.id === office._id)?.startDate || ''}
                                                                        onChange={(e) => handleOfficeDetailChange(office._id, 'startDate', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking Till"
                                                                        value={formData.selectedOffices.find(o => o.id === office._id)?.endDate || ''}
                                                                        onChange={(e) => handleOfficeDetailChange(office._id, 'endDate', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className=''>
                                                            <div className="flex items-center gap-2 duration-300">
                                                                <Radio
                                                                    disabled={office.isPartiallyAssigned}
                                                                    checked={formData.selectedOffices.find(o => o.id === office._id)?.isFullTime || false}
                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'isFullTime', e.target.checked)}
                                                                    label="Full Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                                <Radio
                                                                    checked={!formData.selectedOffices.find(o => o.id === office._id)?.isFullTime || false}
                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'isFullTime', !e.target.checked)}
                                                                    label="Part Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                            </div>
                                                            <div className="w-full flex items-center gap-2 col-span-2 animate-in">
                                                                {
                                                                    !formData.selectedOffices.find(o => o.id === office._id)?.isFullTime && (
                                                                        <div className="w-full border rounded-lg p-2">
                                                                            <span className="text-gray-600 text-sm font-bold">Daily Slot</span>
                                                                            <hr className="my-1" />
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">From</div>
                                                                                <select
                                                                                    value={formData.selectedOffices.find(o => o.id === office._id)?.startTime || ''}
                                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'startTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">To</div>
                                                                                <select
                                                                                    value={formData.selectedOffices.find(o => o.id === office._id)?.endTime || ''}
                                                                                    onChange={(e) => handleOfficeDetailChange(office._id, 'endTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className='border rounded-lg p-2'>
                                                            <div className="text-gray-600 text-sm font-bold">Bookings</div>
                                                            <hr className="my-1" />
                                                            <div className='h-[100px] overflow-y-auto'>
                                                                {
                                                                    office.bookings?.slice(0, 10).map((booking, index) => (
                                                                        <div key={index} className='flex justify-between items-center'>
                                                                            <div className='text-xs'>
                                                                                {booking.dailyStartTime ?
                                                                                    `${moment(booking.dailyStartTime).format('hh:mm a')} - ${moment(booking.dailyEndTime).format('hh:mm a')}` :
                                                                                    'Full Time'}
                                                                                <div className='text-xs'>
                                                                                    {booking.endDate ? moment(booking.endDate).format('ll') : 'Open Ended'}
                                                                                </div>
                                                                            </div>
                                                                            <div className='text-xs'>
                                                                                <Link to={`/customer/${booking?.contract?.user?._id}`} className='flex items-center gap-1 text-blue-600 hover:underline duration-300' target='_blank'>
                                                                                    <ExternalLinkIcon className='w-3 h-3' />{booking?.contract?.user?.name || 'Deleted User'}
                                                                                </Link>
                                                                            </div>
                                                                        </div>
                                                                    )) || <div className="text-gray-600 text-sm">No bookings yet</div>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    )
                                })}
                        </List>
                    )
                }
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                {isUpdating && isDirty && (
                    <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                        {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                    </Button>
                )}
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={checkSelectedOfficesSpecifiedRent.length > 0} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const renderSeatSelection = () => (
        <div className="mt-4 min-h-[480px]">
            <Input
                label="Search Seats"
                icon={<SearchIcon className="h-4 w-4" />}
                value={searchTerm}
                disabled={isLoadingSeats}
                onChange={(e) => setSearchTerm(e.target.value)}
            />
            <div className="mt-2 flex items-center justify-end">
                <Checkbox
                    checked={showAvailableOnly}
                    onChange={(e) => setShowAvailableOnly(e.target.checked)}
                    label={<span className="text-sm text-gray-700">Show available seats only</span>}
                    size="sm"
                />
            </div>
            <div className="h-[390px] overflow-y-auto mt-4">
                {
                    isLoadingSeats ? (
                        <div className="flex justify-center items-center h-full">
                            <Spinner className="w-4 h-4" />
                        </div>
                    ) : (
                        <List>
                            {availableFilteredSeats
                                .slice() // Create a copy to avoid mutating the original array
                                .map((seat) => {
                                    const activeBookings = (seat.bookings || []).filter(booking => {
                                        const isStatusAllowed = !booking?.status || ACTIVE_BOOKING_STATUSES.includes(booking.status);
                                        return isStatusAllowed && !booking.freeze?.isFrozen;
                                    });
                                    return (
                                        <Fragment key={seat._id}>
                                            <ListItem
                                                className="py-1"
                                                // disabled={availability === 0}
                                                selected={formData.selectedSeats.some(s => s.id === seat._id)}
                                                onClick={() => handleSeatSelection(seat._id)}>
                                                <div className="flex w-full items-center">
                                                    <div className="hidden md:block">
                                                        <Checkbox
                                                            checked={formData.selectedSeats.some(s => s.id === seat._id)}
                                                            className="h-4 w-4"
                                                            ripple={false}
                                                            readOnly
                                                        />
                                                    </div>
                                                    <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full">
                                                        <div className="font-thin">
                                                            Seat # {seat.seatCode} ({seat.seatType})
                                                            {activeBookings.length > 0 && (() => {
                                                                const hasFull = activeBookings.some(b => isBookingFullTime(b));
                                                                const hasPart = activeBookings.some(b => !isBookingFullTime(b));
                                                                let badgeText = `${activeBookings.length} active booking(s)`;
                                                                let badgeColor = "text-amber-600";
                                                                if (hasFull && hasPart) {
                                                                    badgeText = "Full & Part-time booked";
                                                                    badgeColor = "text-red-500";
                                                                } else if (hasFull) {
                                                                    badgeText = "Full-time booked";
                                                                    badgeColor = "text-red-500";
                                                                } else {
                                                                    badgeText = `${activeBookings.length} part-time booking(s)`;
                                                                    badgeColor = "text-amber-600";
                                                                }
                                                                return (
                                                                    <span className={`ml-2 font-medium text-xs ${badgeColor}`}>
                                                                        · {badgeText}
                                                                    </span>
                                                                );
                                                            })()}
                                                            <br />
                                                            <span className="text-xs">{seat.monthlyRate} Per Month - {convertToPresentableText(seat.seatType)} - {convertToPresentableText(seat.deskType)} Desk</span>
                                                        </div>
                                                        {/* <div className="font-thin text-xs text-gray-600 text-end">
                                                            {availability === 0 ? "Not Available" : `Available ${availability}h`} <br />
                                                            {getOrdinal(seat.coworking.floor.number)} Floor in {" "}
                                                            {seat.coworking.floor.building.name}, {seat.coworking.floor.building.branch.name} Branch
                                                        </div> */}
                                                    </div>
                                                </div>
                                            </ListItem>
                                            {formData.selectedSeats.some(o => o.id === seat._id) && (
                                                <div className="border-l-2 border-gray-400 ml-2 pl-2">
                                                    <div className={`grid grid-cols-1 md:grid-cols-5 gap-2 mb-2`}>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Monthly Rent"
                                                                placeholder={seat.monthlyRate}
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.monthlyRent || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'monthlyRent', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Advance"
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.advanceAmount || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'advanceAmount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full">
                                                            <Input
                                                                type="number"
                                                                label="Security"
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.securityAmount || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'securityAmount', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="w-full col-span-2">
                                                            <Input
                                                                type="text"
                                                                label="Additional Comments"
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.comment || ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'comment', e.target.value)}
                                                            />
                                                        </div>

                                                    </div>
                                                    {formData.selectedSeats.find(o => o.id === seat._id)?.startDate &&
                                                        moment(formData.selectedSeats.find(o => o.id === seat._id)?.startDate).date() !== 1 && (
                                                        <div className="mb-2">
                                                            <Input
                                                                type="number"
                                                                label="First Month Rent (PKR)"
                                                                placeholder="Leave blank to use standard rent"
                                                                value={formData.selectedSeats.find(o => o.id === seat._id)?.firstMonthRent ?? ''}
                                                                onChange={(e) => handleSeatDetailChange(seat._id, 'firstMonthRent', e.target.value)}
                                                                onFocus={() => handleSeatFirstMonthRentFocus(seat._id)}
                                                            />
                                                            <p className="text-xs text-gray-500 mt-1">
                                                                Enter a prorated amount if this booking starts mid-month.
                                                            </p>
                                                        </div>
                                                    )}
                                                    <div className={`grid grid-cols-1 md:grid-cols-3 gap-2 mb-2`}>
                                                        <div className={`flex flex-col md:flex-row gap-2 w-full`}>

                                                            <div className={`p-2 border rounded-lg w-full`}>
                                                                <span className="text-gray-600 text-sm font-bold">Booking Period</span>
                                                                <hr className="my-1" />
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking From"
                                                                        value={formData.selectedSeats.find(o => o.id === seat._id)?.startDate || ''}
                                                                        onChange={(e) => handleSeatDetailChange(seat._id, 'startDate', e.target.value)}
                                                                    />
                                                                </div>
                                                                <div className={`mt-2`}>
                                                                    <Input
                                                                        type="date"
                                                                        label="Booking Till"
                                                                        value={formData.selectedSeats.find(o => o.id === seat._id)?.endDate || ''}
                                                                        onChange={(e) => handleSeatDetailChange(seat._id, 'endDate', e.target.value)}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className=''>
                                                            <div className="flex items-center gap-2 duration-300">
                                                                <Radio
                                                                    disabled={seat.isPartiallyAssigned}
                                                                    checked={formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime || false}
                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'isFullTime', e.target.checked)}
                                                                    label="Full Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                                <Radio
                                                                    checked={!formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime || false}
                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'isFullTime', !e.target.checked)}
                                                                    label="Part Time"
                                                                    className="h-4 w-4 m-0"
                                                                />
                                                            </div>
                                                            <div className="w-full flex items-center gap-2 col-span-2 animate-in">
                                                                {
                                                                    !formData.selectedSeats.find(o => o.id === seat._id)?.isFullTime && (
                                                                        <div className="w-full border rounded-lg p-2">
                                                                            <span className="text-gray-600 text-sm font-bold">Daily Slot</span>
                                                                            <hr className="my-1" />
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">From</div>
                                                                                <select
                                                                                    value={formData.selectedSeats.find(o => o.id === seat._id)?.startTime || ''}
                                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'startTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                            <div className="flex justify-between items-center gap-2">
                                                                                <div className="text-gray-600 text-sm">To</div>
                                                                                <select
                                                                                    value={formData.selectedSeats.find(o => o.id === seat._id)?.endTime || ''}
                                                                                    onChange={(e) => handleSeatDetailChange(seat._id, 'endTime', e.target.value)}
                                                                                >
                                                                                    {
                                                                                        HOURLY_TIME_SLOTS_ENUM.map((pair) => (
                                                                                            <option key={pair.key} value={pair.key}>{pair.value}</option>
                                                                                        ))
                                                                                    }
                                                                                </select>
                                                                            </div>
                                                                        </div>
                                                                    )
                                                                }
                                                            </div>
                                                        </div>
                                                        <div className='border rounded-lg p-2'>
                                                            <div className="text-gray-600 text-sm font-bold">Active Bookings</div>
                                                            <hr className="my-1" />
                                                            <div className='h-[100px] overflow-y-auto'>
                                                                {
                                                                    seat.bookings?.slice(0, 10).map((booking, index) => (
                                                                        <div key={index} className='flex justify-between items-center'>
                                                                            <div className='text-xs text-gray-600'>
                                                                                {booking.dailyStartTime ?
                                                                                    `${moment(booking.dailyStartTime).format('hh:mm a')} - ${moment(booking.dailyEndTime).format('hh:mm a')}` :
                                                                                    'Full Time'}
                                                                                <div className='text-xs'>
                                                                                    {booking.endDate ? moment(booking.endDate).format('ll') : 'Open Ended'}
                                                                                </div>
                                                                            </div>
                                                                            <div className='text-xs text-gray-600'>
                                                                                <Link to={`/customer/${booking?.contract?.user?._id}`} className='flex items-center gap-1 text-blue-600 hover:underline duration-300' target='_blank'>
                                                                                    <ExternalLinkIcon className='w-3 h-3' />{booking?.contract?.user?.name || 'Deleted User'}
                                                                                </Link>
                                                                            </div>
                                                                        </div>
                                                                    )) || <div className="text-gray-600 text-sm">No bookings yet</div>
                                                                }
                                                            </div>
                                                        </div>
                                                        {/* Inline time conflict warning */}
                                                        {(() => {
                                                            const timeConflict = computeSeatTimeConflict(seat._id);
                                                            return timeConflict ? (
                                                                <div className="col-span-3 border-l-4 border-red-500 bg-red-50 p-2 mt-1 rounded text-sm text-red-700">
                                                                    ⚠ Time conflict: {timeConflict}
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </div>
                                                </div>
                                            )}
                                        </Fragment>
                                    )
                                }
                                )}
                        </List>
                    )
                }
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                {isUpdating && isDirty && (
                    <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                        {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                    </Button>
                )}
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button disabled={((!isUpdating && !formData.selectedOffices.length && !formData.selectedSeats.length) || checkSelectedSeatsSpecifiedRent.length > 0)} onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const handleRemoveOneTimeOtherCharge = (index) => {
        const charges = [...formData.oneTimeOtherCharges];
        charges.splice(index, 1);
        setFormData({
            ...formData,
            oneTimeOtherCharges: charges
        });
    };

    const handleAddOneTimeOtherCharge = () => {
        setFormData({
            ...formData,
            oneTimeOtherCharges: [
                { name: '', description: '', amount: 0, note: '', dueDate: '' },
                ...formData.oneTimeOtherCharges,
            ]
        });
    };

    const handleInputChangeForOneTimeOtherCharge = (index, e) => {
        const { name, value } = e.target;
        console.log('name', name);
        console.log('value', value);
        const charges = [...formData.oneTimeOtherCharges];
        charges[index][name] = value;
        setFormData({
            ...formData,
            oneTimeOtherCharges: charges
        });
    };

    const renderPaymentSelection = () => (
        <div className='space-y-2'>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className='w-full'>
                    <Input
                        label="Total Security (from Bookings)"
                        readOnly
                        type="number"
                        value={summary.totalSecurity || 0}
                    />
                </div>
                <div className='w-full'>
                    <Input
                        label="Total Advance (from Bookings)"
                        readOnly
                        type="number"
                        value={summary.totalAdvance || 0}
                    />
                </div>
            </div>
            <div className='grid grid-cols-1 md:grid-cols-1 gap-2'>
                <div className='w-full'>
                    <Input
                        label="One Time Service Charges"
                        name="oneTimeServiceCharges"
                        type="number"
                        value={formData.oneTimeServiceCharges}
                        onChange={handleInputChange}
                    />
                </div>
            </div>
            <div className='space-y-2 border border-blue-gray-200 rounded-lg p-2'>
                <div className='flex justify-between items-center'>
                    <div className='text-sm text-gray-600'>
                        Any Custom Charges:
                    </div>
                    <div className='text-sm text-gray-600'>
                        <Button onClick={handleAddOneTimeOtherCharge} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                            <PlusIcon className='w-4 h-4' /> Add
                        </Button>
                    </div>
                </div>
                <hr className='my-1' />
                <div className='space-y-2 h-[270px] overflow-y-auto '>
                    {
                        formData.oneTimeOtherCharges?.length > 0 ? (
                            <>
                                {formData.oneTimeOtherCharges?.map((charge, i) => (
                                    <div key={i} className='grid grid-cols-1 md:grid-cols-4 gap-2 mt-1'>
                                        <div className=''>
                                            <Input
                                                label={'Purpose'}
                                                name="name"
                                                value={charge.name}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        <div className=''>
                                            <Input
                                                label={'Description'}
                                                name="description"
                                                type="text"
                                                value={charge.description}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        <div className=''>
                                            <Input
                                                label={'Amount'}
                                                name="amount"
                                                type="number"
                                                value={charge.amount}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                        </div>
                                        {/* <div className=''>
                                <Input
                                    label={'Note'}
                                    name={charge.note}
                                    type="text"
                                    value={charge.note}
                                    onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                />
                            </div> */}
                                        <div className='flex items-center  gap-2'>
                                            <Input
                                                label={'Due Date'}
                                                name="dueDate"
                                                type="date"
                                                value={charge.dueDate}
                                                onChange={(e) => handleInputChangeForOneTimeOtherCharge(i, e)}
                                            />
                                            <IconButton onClick={() => handleRemoveOneTimeOtherCharge(i)} size='sm' variant='outlined' className='rounded-full'>
                                                <TrashIcon className='w-4 h-4' />
                                            </IconButton>
                                        </div>
                                    </div>
                                ))}
                            </>
                        ) : (
                            <div className='text-gray-600 text-sm text-center py-2'>No custom charges added</div>
                        )
                    }
                </div>
            </div>
            <hr className='my-4' />
            <div className="flex justify-end gap-2">
                {isUpdating && isDirty && (
                    <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                        {loadingAddContract && <Spinner className="w-4 h-4" />} Update
                    </Button>
                )}
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button onClick={handleNext} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    Next Step <ArrowRightIcon className='w-4 h-4' /> {availableTabs[activeStep + 1].label}
                </Button>
            </div>
        </div>
    )

    const renderSummary = () => (
        <div className="space-y-4">
            <div className="space-y-2">
                {
                    formData.contractType !== CONNECTION_TYPES.DAY_PASS ? (

                        <div className={`flex overflow-x-auto snap-x snap-mandatory pe-4`}>
                            {
                                formData.selectedOffices.length === 0 ? <></> : (
                                    <div className='flex flex-col items-center justify-center gap-2 border p-1 flex-shrink-0 snap-center border-blue-100 border-t-8 border-r-1 border-l-8 border-b-1  bg-blue-50'>
                                        <div className='text-sm text-blue-500'>Offices</div>
                                        <div className='text-2xl font-bold text-blue-500'>{formData.selectedOffices.length}</div>
                                    </div>
                                )
                            }
                            {
                                formData.selectedOffices.map(office => (
                                    <div key={office._id} className='flex-shrink-0'>
                                        <div className='border border-4 border-blue-100'></div>
                                        <div className="border rounded-lg p-1 flex-shrink-0 snap-center border-blue-100 mt-1 mx-1">
                                            <table className='w-full table-auto'>
                                                <thead>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                                        <th colSpan={2} className='py-1 px-2'>Office: {office.name}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Rent</td>
                                                        <td className='font-thin py-1 px-2'>PKR {formatNumberWithCommas(office.monthlyRent || 0)} </td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Security</td>
                                                        <td className='font-thin py-1 px-2'>PKR {formatNumberWithCommas(office.securityAmount || 0)} </td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Advance</td>
                                                        <td className='font-thin py-1 px-2'>PKR {formatNumberWithCommas(office.advanceAmount || 0)} </td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>From</td>
                                                        <td className='font-thin py-1 px-2'>{office.startDate || formData.startDate || <span className='text-red-500'>Not set</span>}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Till</td>
                                                        <td className='font-thin py-1 px-2'>{office?.endDate || 'Not set'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Comments</td>
                                                        <td className='font-thin py-1 px-2'>{office.comment}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                            }
                            {
                                formData.selectedSeats.length === 0 ? <></> : (
                                    <div className='text-center flex flex-col items-center justify-center gap-2 border p-1 flex-shrink-0 snap-center border-pink-100 border-t-8 border-r-1 border-l-8 border-b-1  bg-pink-50 ms-1'>
                                        <div className='text-sm text-pink-500'>Seats</div>
                                        <div className='text-2xl font-bold text-pink-500'>{formData.selectedSeats.length}</div>
                                    </div>
                                )
                            }
                            {
                                formData.selectedSeats.map(seat => (
                                    <div key={seat._id} className='flex-shrink-0'>
                                        <div className='border border-4 border-pink-100'></div>
                                        <div className="border rounded-lg p-1 flex-shrink-0 snap-center border-pink-100 mt-1 mx-1">
                                            <table className='w-full table-auto'>
                                                <thead>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                                        <th colSpan={2} className='py-1 px-2 text-pink-500'>Seat: {seat.name}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Rent</td>
                                                        <td className='font-thin py-1 px-2'>{seat.monthlyRent ? `PKR ${formatNumberWithCommas(seat.monthlyRent || 0)}` : 'NA'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Security</td>
                                                        <td className='font-thin py-1 px-2'>{`PKR ${formatNumberWithCommas(seat.securityAmount || 0)}`}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Advance</td>
                                                        <td className='font-thin py-1 px-2'>{`PKR ${formatNumberWithCommas(seat.advanceAmount || 0)}`}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>From</td>
                                                        <td className='font-thin py-1 px-2'>{seat.startDate || formData.startDate || <span className='text-red-500'>Not set</span>}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Till</td>
                                                        <td className='font-thin py-1 px-2'>{seat?.endDate || 'Not set'}</td>
                                                    </tr>
                                                    <tr className="even:bg-blue-gray-50/50 text-sm">
                                                        <td className='font-bold py-1 px-2'>Comments</td>
                                                        <td className='font-thin py-1 px-2'>{seat.comment}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                ))
                            }
                        </div>) : (
                        <div className='border rounded-lg border-green-100 p-4 bg-green-50 grid grid-cols-1 md:grid-cols-2 gap-2'>
                            <div>
                                <table className=' table-auto'>
                                    <tbody>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Pass Type</td>
                                            <td className='font-bold py-1 px-2 text-green-500'>{convertToPresentableText(formData.dayPass.passType)}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Booking Required</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.bookingRequired ? 'Yes' : 'No'}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Per Slot Hours</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.perSlotCapacity}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Daily Available Hours</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.dailyCapacity}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Includes Meeting Room</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.includesMeetingRoom ? 'Yes' : 'No'}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <div>
                                <table className='table-auto'>
                                    <tbody>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Max Days Per Month</td>
                                            <td className='font-thin py-1 px-2'>{formData.dayPass.maxDaysPerMonth}</td>
                                        </tr>
                                        <tr className="text-sm">
                                            <td className='font-bold py-1 px-2'>Rent</td>
                                            <td className='font-thin py-1 px-2'>PKR {formData.dayPass.totalPrice || <span className='text-red-500'>0</span>}/{formData.dayPass.priceType}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-2'>
                    <div className='border rounded-lg'>
                        <table className='w-full table-auto'>
                            <tbody>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Customer</td>
                                    <td className='font-thin py-1 px-2'>
                                        {
                                            customer ?
                                                <Link to={`/customer/${customer?._id}`} target='_blank' className='flex items-center gap-2 text-blue-500 font-bold hover:underline duration-300 cursor-pointer'>
                                                    {convertToPresentableText(customer?.name) || 'Deleted Customer'} <ExternalLinkIcon className='w-4 h-4' />
                                                </Link>
                                                :
                                                formData?.user ?
                                                    <Link to={`/customer/${formData.user?._id}`} target='_blank' className='flex items-center gap-2 text-blue-500 font-bold hover:underline duration-300 cursor-pointer'>
                                                        {convertToPresentableText(formData.user?.name) || 'N/A'} <ExternalLinkIcon className='w-4 h-4' />
                                                    </Link>
                                                    :
                                                    <span className='text-red-500'>Not Selected</span>
                                        }
                                    </td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Type</td>
                                    <td className='font-thin py-1 px-2'>{convertToPresentableText(formData.contractType)}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Notice Period</td>
                                    <td className='font-thin py-1 px-2'>{formData.noticePeriod} Weeks</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Duration</td>
                                    <td className='font-thin py-1 px-2'>{moment(formData.startDate).format('ll') || <span className='text-red-500'>Not set</span>} - {formData.endDate ? moment(formData.endDate).format('ll') : <span className='text-gray-700 text-xs'>Open (with {formData.noticePeriod} Weeks Notice)</span>}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Purpose / Reason</td>
                                    <td className='font-thin py-1 px-2'>{formData.reason}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className='border rounded-lg'>
                        <table className='table-auto w-full'>
                            <thead>
                                <tr className="even:bg-blue-gray-50/50 text-sm border-b">
                                    <th className='py-1 px-2 text-left'>Summary</th>
                                    <th className='py-1 px-2 text-left'>Amount</th>
                                    <th className='py-1 px-2 text-left'>Rate</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Security</td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.totalSecurity ?
                                            `PKR ${formatNumberWithCommas(summary.totalSecurity)}` :
                                            <span className='text-red-500 text-sm'>(Please specify Security)</span>}
                                    </td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.recommendedSecurity ?
                                            `PKR ${formatNumberWithCommas(summary.recommendedSecurity)} (2 x Rent)`
                                            : <span className='text-red-500 text-sm'>N/A</span>}
                                    </td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Advance</td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.totalAdvance ?
                                            `PKR ${formatNumberWithCommas(summary.totalAdvance)}` :
                                            <span className='text-red-500 text-sm'>(Please specify Advance)</span>}
                                    </td>
                                    <td className='font-thin py-1 px-2'>
                                        {summary.totalAdvance ?
                                            `PKR ${formatNumberWithCommas(summary.totalAdvance)}`
                                            : <span className='text-red-500 text-sm'>N/A</span>}
                                    </td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Rent</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalRent ? `PKR ${formatNumberWithCommas(summary.totalRent)}/${summary.rentType}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalRent ? `PKR ${formatNumberWithCommas(summary.totalRent)}/${summary.rentType}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>Total</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalPayable ? `PKR ${formatNumberWithCommas(summary.totalPayable)}` : <span className='text-red-500 text-sm'>(Please add Security, Advance, and select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalPayable ? `PKR ${formatNumberWithCommas(summary.totalPayable)}` : <span className='text-red-500 text-sm'>(Please add Security, Advance, and select Offices or Seats)</span>}</td>
                                </tr>
                                <tr className="even:bg-blue-gray-50/50 text-sm">
                                    <td className='font-bold py-1 px-2'>1st Month</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalPayable ? `PKR ${formatNumberWithCommas(summary.totalPayable)}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                    <td className='font-thin py-1 px-2'>{summary.totalPayable ? `PKR ${formatNumberWithCommas(summary.totalPayable)}` : <span className='text-red-500 text-sm'>(Please select Offices or Seats)</span>}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <div className={`flex justify-end gap-2 mt-2`}>
                <Button onClick={handlePrev} size='sm' variant='outlined' className='rounded-full flex items-center gap-2'>
                    {availableTabs[activeStep - 1].label}<ArrowLeftIcon className='w-4 h-4' /> Previous Step
                </Button>
                <Button type="submit" size='sm' className="rounded-full flex items-center gap-2" disabled={loadingAddContract}>
                    {loadingAddContract && <Spinner className="w-4 h-4" />} {isUpdating ? "Update" : "Create Contract"}
                </Button>
            </div>
        </div>
    )


    return (
        <>
            <ModalX
                isOpen={isOpen}
                onClose={onClose}
                title={isUpdating ? "Update Contract" : "Add New Contract"}
                maxWidth="xxxl"
            >
                <ConfirmationDialog />
                <form onSubmit={handleFormSubmit}>
                    <div className="w-full px-1 md:px-10 lg:px-24 py-2">
                        <Stepper
                            activeStep={activeStep}
                            isLastStep={(value) => setIsLastStep(value)}
                            isFirstStep={(value) => setIsFirstStep(value)}
                        >
                            {
                                availableTabs.map((tab, index) => (
                                    <Step
                                        key={tab.value}
                                        label={tab.label}
                                    // onClick={() => setActiveStep(index)}
                                    // disabled={tab.value === CONNECTION_TYPES.DAY_PASS && checkSelectedSeatsSpecifiedRent.length > 0}
                                    >
                                        {tab.icon}
                                        <div className="absolute -bottom-[2.5rem] w-max text-center hidden md:block">
                                            <h6 className={`text-sm font-medium ${activeStep === index ? 'text-blue-500' : 'text-gray-500'} flex items-center justify-center gap-2`}>
                                                <span className="hidden xl:block">Step {index + 1}</span>
                                            </h6>
                                            <h6 className={`hidden xl:block text-xs font-normal ${activeStep === index ? 'text-blue-500' : 'text-gray-500'}`}>
                                                {tab.description}
                                            </h6>
                                        </div>
                                    </Step>
                                ))
                            }
                        </Stepper>
                    </div>
                    <hr className="mt-2 md:mt-[3.5rem]" />
                    <div className="w-full text-center my-4 xl:hidden ">
                        {availableTabs[activeStep].description}
                    </div>
                    <div className="min-h-[480px] mt-4">
                        {activeStep === 0 && renderCustomerSelection()}
                        {activeStep === 1 && renderGeneralDetails()}
                        {activeStep === 2 && renderOfficeSelection()}
                        {activeStep === 3 && renderSeatSelection()}
                        {activeStep === 4 && renderPaymentSelection()}
                        {activeStep === 5 && renderSummary()}
                    </div>
                </form >
            </ModalX >
            <ConfirmationDialog />
        </>
    )
}
export default ContractFormModal;